import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Cpu, AlertTriangle, User, Zap, Target, Radio, Crosshair, Shield, ChevronRight } from 'lucide-react';
import { ITEMS } from '@/config/constants';
import LeonDialogue from '@/components/LeonDialogue';
import CONFIG from '@/config/siteConfig';
import { LASER_GRID } from '@/config/missionContent';

const GRID_SIZE = 7;

const generateGrid = () => {
  const grid = Array(GRID_SIZE).fill(null).map(() => Array(GRID_SIZE).fill(false));
  const path = [[0, 0]];
  let r = 0, c = 0;
  while (r !== GRID_SIZE - 1 || c !== GRID_SIZE - 1) {
    const moves = [];
    if (r < GRID_SIZE - 1) moves.push([r + 1, c]);
    if (c < GRID_SIZE - 1) moves.push([r, c + 1]);
    if (r > 0 && Math.random() < 0.2) moves.push([r - 1, c]);
    if (c > 0 && Math.random() < 0.2) moves.push([r, c - 1]);
    const [nr, nc] = moves[Math.floor(Math.random() * moves.length)];
    r = nr;
    c = nc;
    path.push([r, c]);
  }
  const safeSet = new Set(path.map(([pr, pc]) => `${pr},${pc}`));
  for (let ri = 0; ri < GRID_SIZE; ri++) {
    for (let ci = 0; ci < GRID_SIZE; ci++) {
      if (!safeSet.has(`${ri},${ci}`) && Math.random() < 0.4) {
        grid[ri][ci] = true;
      }
    }
  }
  return grid;
};

/* ── Background & Atmosphere ── */
function LaserBackground() {
  const particles = useMemo(() =>
    Array.from({ length: 10 }, (_, i) => ({
      id: i,
      left: `${8 + (i * 9.1) % 84}%`,
      top: `${5 + (i * 13.7) % 90}%`,
      size: 1 + (i % 3),
      duration: 6 + (i % 4) * 2,
      delay: i * 0.7,
      color: i % 3 === 0 ? 'bg-red-500/20' : i % 3 === 1 ? 'bg-orange-500/15' : 'bg-red-400/10',
    })), []);

  return (
    <div className="fixed inset-0 pointer-events-none overflow-hidden">
      {/* Base gradient — dark red corridor */}
      <div className="absolute inset-0 bg-gradient-to-b from-black via-[#0a0205] to-black" />

      {/* Floating particles */}
      {particles.map(p => (
        <motion.div
          key={p.id}
          animate={{ y: [0, -30, 0], x: [0, 10, -10, 0], opacity: [0.2, 0.5, 0.2] }}
          transition={{ duration: p.duration, repeat: Infinity, delay: p.delay, ease: 'easeInOut' }}
          className={`absolute rounded-full ${p.color}`}
          style={{ left: p.left, top: p.top, width: p.size, height: p.size }}
        />
      ))}

      {/* Red vignette */}
      <div
        className="absolute inset-0 animate-pulse"
        style={{
          background: 'radial-gradient(ellipse at center, transparent 40%, rgba(80,0,0,0.15) 100%)',
          animationDuration: '5s',
        }}
      />

      {/* CRT scanlines */}
      <div
        className="absolute inset-0 opacity-[0.015]"
        style={{
          backgroundImage: 'repeating-linear-gradient(0deg, rgba(255,0,0,0.15) 0px, transparent 1px, transparent 3px)',
          backgroundSize: '100% 3px',
        }}
      />
    </div>
  );
}

/* ── Enhanced Laser Overlay ── */
function LaserOverlay({ active }) {
  if (!active) return null;
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none z-10">
      {[0, 1, 2].map(i => (
        <motion.div
          key={i}
          animate={{ x: ['-100%', '200%'] }}
          transition={{ duration: 2 + i * 0.5, repeat: Infinity, delay: i * 0.8, ease: 'linear' }}
          className="absolute h-px"
          style={{
            top: `${20 + i * 30}%`,
            width: '50%',
            background: 'linear-gradient(90deg, transparent, rgba(255,0,0,0.2), transparent)',
          }}
        />
      ))}
      <motion.div
        animate={{ y: ['0%', '100%'] }}
        transition={{ duration: 3, repeat: Infinity, ease: 'linear' }}
        className="absolute inset-x-0 h-16"
        style={{ background: 'linear-gradient(to bottom, transparent, rgba(255,0,0,0.03), transparent)' }}
      />
      {/* Corner laser glows */}
      <div className="absolute top-0 left-0 w-32 h-32" style={{ background: 'radial-gradient(circle at 0% 0%, rgba(255,0,0,0.06) 0%, transparent 70%)' }} />
      <div className="absolute bottom-0 right-0 w-32 h-32" style={{ background: 'radial-gradient(circle at 100% 100%, rgba(255,0,0,0.06) 0%, transparent 70%)' }} />
    </div>
  );
}

/* ── HUD Corners ── */
function HUDCorners() {
  return (
    <>
      <div className="absolute top-0 left-0 w-6 h-6 border-t border-l border-red-700/30" />
      <div className="absolute top-0 right-0 w-6 h-6 border-t border-r border-red-700/30" />
      <div className="absolute bottom-0 left-0 w-6 h-6 border-b border-l border-red-700/30" />
      <div className="absolute bottom-0 right-0 w-6 h-6 border-b border-r border-red-700/30" />
    </>
  );
}

/* ── Tutorial Screen ── */
function LaserTutorial({ onStart }) {
  const symbols = useMemo(() => [
    { icon: User, label: 'PLAYER', desc: 'Your position', color: 'text-green-400', bg: 'bg-green-500/10', border: 'border-green-600/30' },
    { icon: Target, label: 'EXIT', desc: 'Destination', color: 'text-yellow-400', bg: 'bg-yellow-500/10', border: 'border-yellow-600/30' },
    { icon: Crosshair, label: 'SAFE', desc: 'Visited path', color: 'text-green-600', bg: 'bg-green-500/5', border: 'border-green-800/30' },
    { icon: Zap, label: 'LASER', desc: 'Hidden trap', color: 'text-red-500', bg: 'bg-red-500/10', border: 'border-red-600/30' },
  ], []);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="max-w-md mx-auto relative z-10"
    >
      <div className="bg-black/60 backdrop-blur-sm border border-red-900/30 rounded-lg p-6 relative">
        <HUDCorners />

        {/* Header */}
        <div className="text-center mb-5">
          <div className="flex items-center justify-center gap-2 mb-2">
            <Zap size={14} className="text-red-500" />
            <span className="font-terminal text-xs tracking-[4px] text-red-500 uppercase">Corridor Protocol</span>
            <Zap size={14} className="text-red-500" />
          </div>
          <div className="w-16 h-px bg-gradient-to-r from-transparent via-red-700/50 to-transparent mx-auto" />
        </div>

        {/* How it works */}
        <div className="mb-5">
          <h3 className="font-terminal text-xs tracking-[3px] text-red-400 uppercase mb-2">How It Works</h3>
          <div className="space-y-1.5">
            {[
              'Click adjacent cells to move through the grid',
              'Reach the EXIT at the bottom-right corner',
              'Hidden lasers are scattered throughout the corridor',
            ].map((text, i) => (
              <div key={i} className="flex items-start gap-2">
                <ChevronRight size={10} className="text-red-600 mt-0.5 shrink-0" />
                <span className="text-xs text-gray-400 font-rajdhani">{text}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Symbols guide */}
        <div className="mb-5">
          <h3 className="font-terminal text-xs tracking-[3px] text-red-400 uppercase mb-2">Symbols Guide</h3>
          <div className="grid grid-cols-2 gap-2">
            {symbols.map(s => (
              <div key={s.label} className={`flex items-center gap-2 ${s.bg} border ${s.border} rounded px-2.5 py-1.5`}>
                <s.icon size={12} className={s.color} />
                <div>
                  <div className={`text-xs font-terminal tracking-wider ${s.color}`}>{s.label}</div>
                  <div className="text-xs text-gray-500 font-rajdhani">{s.desc}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Warning */}
        <div className="flex items-center gap-2 bg-red-950/20 border border-red-800/30 rounded px-3 py-2 mb-5">
          <AlertTriangle size={12} className="text-red-500 shrink-0" />
          <span className="text-xs text-red-400 font-terminal tracking-wider">HITTING A LASER RESETS YOU TO START</span>
        </div>

        {/* Start button */}
        <motion.button
          onClick={onStart}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          className="w-full py-3 bg-gradient-to-r from-red-900/40 via-red-800/50 to-red-900/40 border border-red-700/40 rounded font-terminal text-sm tracking-[4px] text-red-300 uppercase relative overflow-hidden group"
        >
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-red-500/10 to-transparent translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-700" />
          <span className="relative flex items-center justify-center gap-2">
            <Zap size={14} />
            Enter Corridor
            <Zap size={14} />
          </span>
          <motion.div
            animate={{ opacity: [0.3, 0.6, 0.3] }}
            transition={{ duration: 2, repeat: Infinity }}
            className="absolute inset-0 border border-red-500/20 rounded"
          />
        </motion.button>
      </div>
    </motion.div>
  );
}

/* ── Enhanced HUD ── */
function SecurityHUD({ resets, steps, active, hitLaser }) {
  const threatLevel = resets > 3 ? 'CRITICAL' : resets > 1 ? 'ELEVATED' : 'NOMINAL';
  const threatColor = resets > 3 ? 'text-red-400' : resets > 1 ? 'text-yellow-400' : 'text-green-400';

  return (
    <div className="max-w-lg mx-auto mb-4 relative z-10">
      <div className="bg-black/50 border border-red-900/30 rounded px-4 py-2">
        {/* Top row */}
        <div className="flex items-center justify-between mb-1.5">
          <span className="text-xs font-terminal text-red-700/60 tracking-[3px]">CHECKPOINT 06 // LASER CORRIDOR</span>
          <div className="flex items-center gap-1.5">
            <AlertTriangle size={9} className={threatColor} />
            <span className={`text-xs font-terminal tracking-wider ${threatColor}`}>{threatLevel}</span>
          </div>
        </div>

        {/* Bottom row — stats */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            {/* Steps */}
            <div className="flex items-center gap-1.5">
              <motion.div
                animate={active ? { opacity: [1, 0.5, 1] } : { opacity: 1 }}
                transition={{ duration: 1.5, repeat: Infinity }}
              >
                <Radio size={10} className={hitLaser ? 'text-red-500' : 'text-red-600'} />
              </motion.div>
              <span className="text-xs font-terminal text-gray-600">STEPS:</span>
              <span className="text-sm font-orbitron text-red-400">{steps}</span>
            </div>

            {/* Resets */}
            <div className="flex items-center gap-1.5">
              <span className="text-xs font-terminal text-gray-600">RESETS:</span>
              <div className="flex items-center gap-1">
                {resets === 0 ? (
                  <span className="text-sm font-orbitron text-green-400">0</span>
                ) : (
                  Array.from({ length: Math.min(resets, 5) }, (_, i) => (
                    <div
                      key={i}
                      className={`w-2 h-2 rounded-full ${i < 2 ? 'bg-yellow-500' : i < 4 ? 'bg-orange-500' : 'bg-red-500'}`}
                      style={{ boxShadow: `0 0 4px ${i < 2 ? 'rgba(234,179,8,0.5)' : i < 4 ? 'rgba(249,115,22,0.5)' : 'rgba(239,68,68,0.5)'}` }}
                    />
                  ))
                )}
                {resets > 5 && <span className="text-xs font-terminal text-red-500">+{resets - 5}</span>}
              </div>
            </div>
          </div>

          {/* Danger warning */}
          {resets > 2 && (
            <motion.div
              animate={{ opacity: [0.4, 1, 0.4] }}
              transition={{ duration: 1.2, repeat: Infinity }}
              className="flex items-center gap-1"
            >
              <Shield size={9} className="text-red-500" />
              <span className="text-xs font-terminal text-red-500 tracking-wider">DANGER</span>
            </motion.div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ── Main Component ── */
export default function LaserGrid({ onComplete, onItemCollected }) {
  const [showLeon, setShowLeon] = useState(true);
  const [showTutorial, setShowTutorial] = useState(false);
  const [active, setActive] = useState(false);
  const [playerPos, setPlayerPos] = useState([0, 0]);
  const [visited, setVisited] = useState(new Set(['0,0']));
  const [hitLaser, setHitLaser] = useState(false);
  const [solved, setSolved] = useState(false);
  const [showFinalLeon, setShowFinalLeon] = useState(false);
  const [resets, setResets] = useState(0);
  const [successPhase, setSuccessPhase] = useState(0);
  const [shaking, setShaking] = useState(false);
  const [cellsVisible, setCellsVisible] = useState(false);
  const [showProceed, setShowProceed] = useState(false);
  const stepsRef = useRef(0);
  const [steps, setSteps] = useState(0);

  const grid = useMemo(() => generateGrid(), []);

  // Mount-reset
  useEffect(() => {
    setShowLeon(true);
    setShowTutorial(false);
    setActive(false);
    setPlayerPos([0, 0]);
    setVisited(new Set(['0,0']));
    setHitLaser(false);
    setSolved(false);
    setShowFinalLeon(false);
    setResets(0);
    setSuccessPhase(0);
    setShaking(false);
    setCellsVisible(false);
    setShowProceed(false);
    stepsRef.current = 0;
    setSteps(0);
  }, []);

  const handleLeonDismiss = useCallback(() => {
    setShowLeon(false);
    setShowTutorial(true);
  }, []);

  const handleTutorialStart = useCallback(() => {
    setShowTutorial(false);
    setActive(true);
    // Stagger cell entrance
    setTimeout(() => setCellsVisible(true), 50);
  }, []);

  const isAdjacent = useCallback((r, c) => {
    const [pr, pc] = playerPos;
    return (Math.abs(pr - r) + Math.abs(pc - c)) === 1;
  }, [playerPos]);

  const handleCellClick = useCallback((r, c) => {
    if (!active || solved || hitLaser) return;
    if (!isAdjacent(r, c)) return;

    stepsRef.current += 1;
    setSteps(stepsRef.current);

    if (grid[r][c]) {
      setHitLaser(true);
      setShaking(true);
      setTimeout(() => setShaking(false), 500);
      setTimeout(() => {
        setPlayerPos([0, 0]);
        setVisited(new Set(['0,0']));
        setHitLaser(false);
        setResets(prev => prev + 1);
      }, 1000);
      return;
    }

    const key = `${r},${c}`;
    setPlayerPos([r, c]);
    setVisited(prev => new Set([...prev, key]));

    if (r === GRID_SIZE - 1 && c === GRID_SIZE - 1) {
      setSolved(true);
      setActive(false);
      // Multi-phase success
      setSuccessPhase(1);
      setTimeout(() => setSuccessPhase(2), 500);
      setTimeout(() => setSuccessPhase(3), 1200);
      setTimeout(() => setSuccessPhase(4), 1800);
      setTimeout(() => setShowFinalLeon(true), 2200);
    }
  }, [active, solved, hitLaser, grid, isAdjacent]);

  const handleFinalDismiss = useCallback(() => {
    setShowFinalLeon(false);
    onItemCollected?.(ITEMS.ACCESS_CHIP);
    setShowProceed(true);
  }, [onItemCollected]);

  return (
    <div className="puzzle-container relative overflow-hidden" data-testid="puzzle-laser-grid">
      <LaserBackground />
      <LaserOverlay active={active && !solved} />

      {/* Header */}
      <div className="text-center mb-5 relative z-10">
        <div className="flex items-center justify-center gap-2 mb-2">
          <div className="w-8 h-px bg-gradient-to-r from-transparent to-red-700/40" />
          <Zap size={12} className="text-red-500" />
          <p className="text-xs font-terminal tracking-[6px] text-red-500 uppercase">Checkpoint 06</p>
          <Zap size={12} className="text-red-500" />
          <div className="w-8 h-px bg-gradient-to-l from-transparent to-red-700/40" />
        </div>
        <h2 className="font-orbitron text-2xl tracking-wider text-gray-200 uppercase">Laser Corridor</h2>
        <p className="text-sm text-red-700/60 font-terminal mt-1">Security System // Navigate to the exit</p>
      </div>

      {/* Tutorial */}
      <AnimatePresence>
        {showTutorial && <LaserTutorial onStart={handleTutorialStart} />}
      </AnimatePresence>

      {/* HUD */}
      {(active || solved) && (
        <SecurityHUD resets={resets} steps={steps} active={active} hitLaser={hitLaser} />
      )}

      {/* Grid area */}
      {(active || solved) && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex flex-col items-center gap-4 relative z-10">
          {/* Grid container with shake */}
          <div className={`bg-black/50 border border-red-900/20 rounded-lg p-4 relative overflow-hidden ${shaking ? 'animate-shake' : ''}`}>
            <HUDCorners />

            {/* Red flash on laser hit */}
            <AnimatePresence>
              {hitLaser && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: [0, 0.3, 0, 0.25, 0] }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.6 }}
                  className="absolute inset-0 bg-red-500 pointer-events-none z-20 rounded-lg"
                />
              )}
            </AnimatePresence>

            {/* Grid */}
            <div
              className="grid gap-1"
              style={{ gridTemplateColumns: `repeat(${GRID_SIZE}, 1fr)` }}
              data-testid="laser-grid-board"
            >
              {grid.map((row, r) =>
                row.map((isLaser, c) => {
                  const isPlayer = playerPos[0] === r && playerPos[1] === c;
                  const isStart = r === 0 && c === 0;
                  const isEnd = r === GRID_SIZE - 1 && c === GRID_SIZE - 1;
                  const wasVisited = visited.has(`${r},${c}`);
                  const adj = isAdjacent(r, c);
                  const laserRevealed = hitLaser || solved;
                  const cellIndex = r * GRID_SIZE + c;

                  return (
                    <motion.div
                      key={`${r}-${c}`}
                      initial={{ opacity: 0, scale: 0.8 }}
                      animate={cellsVisible ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0.8 }}
                      transition={{ delay: cellIndex * 0.01, duration: 0.2 }}
                      onClick={() => handleCellClick(r, c)}
                      whileHover={adj && active && !hitLaser ? { scale: 1.08, borderColor: 'rgba(255,60,60,0.5)' } : {}}
                      className={`w-9 h-9 md:w-11 md:h-11 rounded-sm flex items-center justify-center text-xs font-terminal border-2 transition-all cursor-pointer relative overflow-hidden
                        ${isPlayer ? 'bg-green-600/20 border-green-500' : ''}
                        ${isEnd && !isPlayer && !solved ? 'bg-yellow-600/10 border-yellow-600/50' : ''}
                        ${isStart && !isPlayer ? 'bg-green-600/10 border-green-600/30' : ''}
                        ${hitLaser && isLaser ? 'bg-red-600/30 border-red-500' : ''}
                        ${!isPlayer && !isEnd && !isStart && !(hitLaser && isLaser) ? (adj && active && !hitLaser ? 'border-red-800/40 bg-red-950/10' : 'border-gray-800/30 bg-black/20') : ''}
                        ${wasVisited && !isPlayer ? 'bg-green-950/20 border-green-900/40' : ''}
                        ${solved && isLaser ? 'bg-red-950/20 border-red-900/30' : ''}
                      `}
                      style={{
                        boxShadow: isPlayer
                          ? '0 0 12px rgba(0,255,65,0.3), inset 0 0 8px rgba(0,255,65,0.1)'
                          : wasVisited && !isPlayer
                          ? '0 0 4px rgba(0,255,65,0.1)'
                          : adj && active && !hitLaser
                          ? 'inset 0 0 6px rgba(255,60,60,0.1)'
                          : undefined,
                      }}
                      data-testid={`grid-cell-${r}-${c}`}
                    >
                      {/* Player */}
                      {isPlayer && (
                        <motion.div
                          initial={{ scale: 0 }}
                          animate={{ scale: 1 }}
                          transition={{ type: 'spring', stiffness: 400, damping: 15 }}
                          className="relative"
                        >
                          <User size={16} className="text-green-400" style={{ filter: 'drop-shadow(0 0 6px rgba(0,255,65,0.6))' }} />
                          <motion.div
                            animate={{ scale: [1, 1.5, 1], opacity: [0.5, 0, 0.5] }}
                            transition={{ duration: 1.5, repeat: Infinity }}
                            className="absolute inset-0 rounded-full border border-green-500"
                          />
                        </motion.div>
                      )}

                      {/* End marker */}
                      {isEnd && !isPlayer && !solved && (
                        <motion.div
                          animate={{ scale: [1, 1.1, 1] }}
                          transition={{ duration: 2, repeat: Infinity }}
                          className="flex flex-col items-center"
                        >
                          <Target size={14} className="text-yellow-500" style={{ filter: 'drop-shadow(0 0 4px rgba(234,179,8,0.5))' }} />
                          <span className="text-[6px] text-yellow-500 tracking-wider">EXIT</span>
                        </motion.div>
                      )}

                      {/* Start marker */}
                      {isStart && !isPlayer && (
                        <span className="text-xs text-green-600 tracking-wider">START</span>
                      )}

                      {/* Laser revealed */}
                      {laserRevealed && isLaser && !isPlayer && (
                        <motion.div
                          initial={{ scale: 0 }}
                          animate={{ scale: 1 }}
                          className="relative"
                        >
                          <div className="w-3 h-3 rounded-full bg-red-500" style={{ boxShadow: '0 0 10px rgba(255,0,0,0.7)' }} />
                          <motion.div
                            animate={{ scale: [1, 2, 1], opacity: [0.5, 0, 0.5] }}
                            transition={{ duration: 1, repeat: Infinity }}
                            className="absolute inset-0 rounded-full border border-red-500"
                          />
                        </motion.div>
                      )}

                      {/* Path trace — visited cells glow */}
                      {wasVisited && !isPlayer && !isStart && !isEnd && (
                        <div
                          className="w-1.5 h-1.5 rounded-full bg-green-500/50"
                          style={{ boxShadow: '0 0 4px rgba(0,255,65,0.3)' }}
                        />
                      )}
                    </motion.div>
                  );
                })
              )}
            </div>
          </div>

          {/* Legend */}
          <div className="flex gap-3 flex-wrap justify-center">
            {[
              { icon: User, label: 'You', color: 'text-green-400', border: 'border-green-600' },
              { icon: Target, label: 'Exit', color: 'text-yellow-400', border: 'border-yellow-600' },
              { icon: Crosshair, label: 'Clickable', color: 'text-red-400', border: 'border-red-800' },
              { icon: Zap, label: 'Laser (Hidden)', color: 'text-red-500', border: 'border-red-800' },
            ].map(item => (
              <div key={item.label} className={`flex items-center gap-1.5 bg-black/40 px-2 py-1 rounded border ${item.border}/30`}>
                <item.icon size={10} className={item.color} />
                <span className={`text-xs font-terminal tracking-wider ${item.color}`}>{item.label.toUpperCase()}</span>
              </div>
            ))}
          </div>

          {/* Laser hit message */}
          <AnimatePresence>
            {hitLaser && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="flex items-center gap-2 bg-red-950/30 border border-red-500/50 rounded px-4 py-2"
              >
                <AlertTriangle size={14} className="text-red-400" />
                <span className="text-sm font-terminal text-red-400 tracking-wider">LASER DETECTED -- RESETTING POSITION</span>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      )}

      {/* Multi-phase success */}
      {solved && (
        <div className="relative z-10 mt-6">
          {/* Phase 1: Green flash + shockwave */}
          {successPhase >= 1 && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: [0.6, 0] }}
              transition={{ duration: 0.5 }}
              className="absolute inset-0 bg-green-500/20 pointer-events-none rounded-lg"
            />
          )}

          <div className="text-center space-y-4">
            {/* Phase 2: Icon */}
            {successPhase >= 2 && (
              <motion.div
                initial={{ scale: 0, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ type: 'spring', stiffness: 300, damping: 15 }}
                className="relative w-20 h-20 mx-auto"
              >
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{ duration: 3, repeat: Infinity, ease: 'linear' }}
                  className="absolute inset-0 border-2 border-dashed border-green-500/20 rounded-full"
                />
                <div
                  className="absolute inset-0 flex items-center justify-center"
                >
                  <div
                    className="w-16 h-16 rounded-full bg-green-900/30 border-2 border-green-500 flex items-center justify-center"
                    style={{ boxShadow: '0 0 30px rgba(0,255,65,0.25)' }}
                  >
                    <Cpu size={28} className="text-green-400" />
                  </div>
                </div>
                {/* Shockwave ring */}
                <motion.div
                  initial={{ scale: 0.5, opacity: 0.8 }}
                  animate={{ scale: 3, opacity: 0 }}
                  transition={{ duration: 1.5, ease: 'easeOut' }}
                  className="absolute inset-0 border border-green-500/40 rounded-full"
                />
              </motion.div>
            )}

            {/* Phase 3: Title */}
            {successPhase >= 3 && (
              <motion.p
                initial={{ letterSpacing: '2px', opacity: 0 }}
                animate={{ letterSpacing: '6px', opacity: 1 }}
                transition={{ duration: 0.8 }}
                className="font-orbitron text-green-400 text-sm"
              >
                CORRIDOR CLEARED
              </motion.p>
            )}

            {/* Phase 4: Stats */}
            {successPhase >= 4 && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="space-y-2"
              >
                <div className="flex items-center justify-center gap-6">
                  <div className="text-center">
                    <div className="text-xs font-terminal text-gray-600 tracking-wider">RESETS</div>
                    <div className={`text-lg font-orbitron ${resets === 0 ? 'text-green-400' : 'text-yellow-400'}`}>{resets}</div>
                  </div>
                  <div className="h-8 w-px bg-gray-800" />
                  <div className="text-center">
                    <div className="text-xs font-terminal text-gray-600 tracking-wider">STEPS</div>
                    <div className="text-lg font-orbitron text-red-400">{steps}</div>
                  </div>
                </div>

                <motion.p
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.3 }}
                  className="text-gray-400 font-rajdhani"
                  style={{ textShadow: '0 0 10px rgba(234,179,8,0.3)' }}
                >
                  Access Chip acquired
                </motion.p>
              </motion.div>
            )}
          </div>
        </div>
      )}

      {showProceed && (
        <motion.div
          initial={{ opacity: 0, y: 15 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ type: 'spring', stiffness: 200, damping: 20 }}
          className="text-center mt-8 relative z-20"
        >
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => onComplete?.()}
            className="inline-flex items-center gap-2 px-8 py-3 rounded font-orbitron text-sm tracking-widest text-black relative overflow-hidden"
            style={{ background: 'linear-gradient(135deg, #22c55e, #00ff41)' }}
          >
            <span className="relative z-10">PROCEED</span>
            <motion.div
              className="absolute inset-0"
              animate={{ x: ['-100%', '200%'] }}
              transition={{ duration: 2, repeat: Infinity, ease: 'linear' }}
              style={{ background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.15), transparent)', width: '50%' }}
            />
          </motion.button>
        </motion.div>
      )}

      {/* Leon dialogues */}
      {showLeon && (
        <LeonDialogue
          message={LASER_GRID.leon.intro}
          onDismiss={handleLeonDismiss}
        />
      )}

      {showFinalLeon && (
        <LeonDialogue
          message={LASER_GRID.leon.complete(resets)}
          onDismiss={handleFinalDismiss}
        />
      )}
    </div>
  );
}
